import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useEffect, type ReactElement } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { AuthService } from '@/services/auth/AuthService'
import { AppShell } from '@/components/layout/AppShell'

import { LoginPage } from '@/pages/Login'
import { DashboardPage } from '@/pages/Dashboard'

import { CompaniesListPage } from '@/pages/Companies/List'
import { MyCompanyDashboardPage } from '@/pages/MyCompany/Dashboard'
import { MyCompanyCollaboratorsPage } from '@/pages/MyCompany/Collaborators'
import { CollaboratorFormPage } from '@/pages/MyCompany/CollaboratorForm'
import { MyCompanyBatchCouponPage } from '@/pages/MyCompany/CollaboratorsBatchCoupon'
import { CompanyFormPage } from '@/pages/Companies/Form'
import { CompanyInvoicesPage } from '@/pages/Companies/Invoices'
import { PlansListPage } from '@/pages/Plans/List'
import { PlanFormPage } from '@/pages/Plans/Form'
import { SubscribersListPage } from '@/pages/Subscribers/List'
import { SubscriberFormPage } from '@/pages/Subscribers/Form'
import { SubscriberInvoicesPage } from '@/pages/Subscribers/Invoices'
import { DiscountCouponsListPage } from '@/pages/DiscountCoupons/List'
import { DiscountCouponFormPage } from '@/pages/DiscountCoupons/Form'
import { ReportsGeneralPage, ReportsAgePage, ReportsLocationPage, ProvidersReportPage } from '@/pages/Reports'
import { CommissionStructuresListPage } from '@/pages/CommissionStructures/List'
import { CommissionStructureFormPage } from '@/pages/CommissionStructures/Form'
import { RevenuesListPage } from '@/pages/Revenues/List'
import { RevenueDetailPage } from '@/pages/Revenues/Detail'
import { RevenueProjectionPage } from '@/pages/Revenues/Projection'
import { RevenueReportEarningsPage } from '@/pages/Revenues/ReportEarnings'
import { RevenueReportCommissionsPage } from '@/pages/Revenues/ReportCommissions'

import { CredentialingProvidersPage } from '@/pages/Credentialing/Providers'
import { CredentialingProviderFormPage } from '@/pages/Credentialing/ProviderForm'
import { CredentialingSpecialtyFilterPage } from '@/pages/Credentialing/SpecialtyFilter'
import { CredentialingReportDashboardPage } from '@/pages/Credentialing/ReportDashboard'
import { CredentialingReportCommissionsPage } from '@/pages/Credentialing/ReportCommissions'
import { CredentialingReportMapPage } from '@/pages/Credentialing/ReportMap'
import { CredentialingKanbanPage } from '@/pages/Credentialing/Kanban'
import { CredentialingStepsPage } from '@/pages/Credentialing/Steps'
import { CredentialingTypesPage } from '@/pages/Credentialing/Types'
import { CredentialingCommissionStructuresPage } from '@/pages/Credentialing/CommissionStructures'
import { CredentialingCommissionStructureFormPage } from '@/pages/Credentialing/CommissionStructureForm'
import { CredentialingDashboardPage } from '@/pages/Credentialing/Dashboard'

import { WorkflowSpecialtiesPage } from '@/pages/Workflow/Specialties'
import { WorkflowTestsPage } from '@/pages/Workflow/Tests'

import { HierarchiesPage } from '@/pages/Administractive/Hierarchies'
import { HierarchyFormPage } from '@/pages/Administractive/HierarchyForm'
import { UsersPage } from '@/pages/Administractive/Users'
import { UserFormPage } from '@/pages/Administractive/UserForm'

import { SettingsIndexPage } from '@/pages/Settings/index'
import { SettingsBanksPage } from '@/pages/Settings/Banks'
import { SettingsParametersPage } from '@/pages/Settings/Parameters'
import { SettingsMaritalStatusesPage } from '@/pages/Settings/MaritalStatuses'
import { SettingsDegreeRelationshipsPage } from '@/pages/Settings/DegreeRelationships'
import { SettingsModalitiesPage } from '@/pages/Settings/Modalities'
import { SettingsServiceProvidersPage } from '@/pages/Settings/ServiceProviders'
import { SettingsReportTemplatesPage } from '@/pages/Settings/ReportTemplates'
import { SettingsReportTemplateFormPage } from '@/pages/Settings/ReportTemplateForm'
import { SettingsProposalPricingPage } from '@/pages/Settings/ProposalPricing'
import { SettingsProposalSetupPage } from '@/pages/Settings/ProposalSetup'
import { SettingsProposalCatalogPage } from '@/pages/Settings/ProposalCatalog'
import { SettingsSupportWhatsAppPage } from '@/pages/Settings/SupportWhatsApp'
import { SettingsIntegrationsMedicarPage } from '@/pages/Settings/IntegrationsMedicar'
import { SettingsProposalTemplatesPage } from '@/pages/Settings/ProposalTemplates'
import { ProposalsListPage } from '@/pages/Proposals/List'
import { ProposalFormPage } from '@/pages/Proposals/Form'
import { ProposalKanbanPage } from '@/pages/Proposals/Kanban'
import { ProposalStagesPage } from '@/pages/Proposals/Stages'
import { SettingsServicesPage } from '@/pages/Settings/Services'
import { SupportInboxPage } from '@/pages/Support/Inbox'
import { SupportSettingsPage } from '@/pages/Support/Settings'
import { SettingsManualBlocksPage } from '@/pages/Settings/ManualBlocks'
import { ProductsListPage } from '@/pages/Settings/Products'
import { ProductFormPage } from '@/pages/Settings/ProductForm'

import { MyCompanyBranchesPage } from '@/pages/MyCompany/Branches'
import { BranchFormPage } from '@/pages/MyCompany/BranchForm'
import { MyCompanyDepartmentsPage } from '@/pages/MyCompany/Departments'
import { DepartmentFormPage } from '@/pages/MyCompany/DepartmentForm'
import { MyCompanyCatalogPage } from '@/pages/MyCompany/Catalog'
import { MyCompanyInvoicesPage } from '@/pages/MyCompany/Invoices'
import { MyCompanyInvoiceDetailPage } from '@/pages/MyCompany/InvoiceDetail'
import { PartnerDashboardPage } from '@/pages/Partner/Dashboard'
import { PartnerInvoicesPage } from '@/pages/Partner/Invoices'
import { PartnerInvoiceDetailPage } from '@/pages/Partner/InvoiceDetail'
import { PartnerReportEarningsPage } from '@/pages/Partner/ReportEarnings'
import { PartnerReportCommissionsPage } from '@/pages/Partner/ReportCommissions'
import { PartnerNetworkPage } from '@/pages/Partner/Network'
import { PartnerNetworkFormPage } from '@/pages/Partner/NetworkForm'
import { PartnerSalesLinkPage } from '@/pages/Partner/SalesLink'
import { PartnerCommissionModelsPage } from '@/pages/Partner/CommissionModels'
import { PartnerCommissionModelFormPage } from '@/pages/Partner/CommissionModelForm'

import { MedicalAppointmentsListPage } from '@/pages/MedicalAppointments/List'
import { IntegrationsMedicarPage } from '@/pages/Integrations/Medicar'
import { IntegrationsDuplicateInvoicesPage } from '@/pages/Integrations/DuplicateInvoices'
import { IntegrationsErrorLogsPage } from '@/pages/Integrations/ErrorLogs'
import { IntegrationsBackupsPage } from '@/pages/Integrations/Backups'
import { AsaasUngeneratedInvoicesPage } from '@/pages/Integrations/AsaasUngeneratedInvoices'
import { AsaasChargeFinderPage } from '@/pages/Integrations/AsaasChargeFinder'

import { SalesLinkPlansPage } from '@/pages/SalesLink/PlansPage'
import { SalesLinkSubscribePage } from '@/pages/SalesLink/SubscribePage'
import { SalesLinkSuccessPage } from '@/pages/SalesLink/SuccessPage'
import { AccessDeniedPage } from '@/pages/AccessDenied'
import { useAuthority } from '@/hooks/useAuthority'
import { permissionForPath } from '@/lib/routePermissions'

function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken)
  const setUser = useAuthStore((s) => s.setUser)
  // Atualiza o usuário (e suas permissões) ao entrar/recarregar o app, para
  // refletir mudanças de hierarquia sem exigir novo login. Falha silenciosa
  // mantém o usuário em cache (401 é tratado pelo interceptor do axios).
  useEffect(() => {
    if (!token) return
    AuthService.me().then(setUser).catch(() => {})
  }, [token, setUser])
  return token ? children : <Navigate to="/login" replace />
}

function RedirectIfAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken)
  return token ? <Navigate to="/" replace /> : children
}

// ============================================================================
// Controle de acesso por tipo de usuário.
//
// REGRA DE SEGURANÇA: empresa e parceiros (diretor/supervisor/afiliado/vendedor)
// NÃO podem acessar nenhuma página administrativa. Cada tipo só enxerga o seu
// próprio painel; qualquer rota fora do escopo redireciona para a "home" do tipo.
// ============================================================================
const ADMIN_TYPES   = ['ADMINISTRADOR', 'COLABORADOR']
const EMPRESA_TYPES = ['EMPRESA']
const PARTNER_TYPES = ['DIRETOR', 'SUPERVISOR', 'AFILIADO', 'VENDEDOR']

/** Rota "home" de cada tipo de usuário. */
function homeFor(type?: string): string {
  if (type && PARTNER_TYPES.includes(type)) return '/partner/dashboard'
  if (type === 'EMPRESA') return '/my-company/dashboard'
  return '/'
}

/** Renderiza o dashboard correto na raiz conforme o tipo (sem vazar o admin). */
function HomeRedirect() {
  const type = useAuthStore((s) => s.user?.type)
  if (type === 'EMPRESA') return <Navigate to="/my-company/dashboard" replace />
  if (type && PARTNER_TYPES.includes(type)) return <Navigate to="/partner/dashboard" replace />
  return <DashboardPage />
}

/**
 * Guarda de permissão do painel: a tela só abre para quem tem a permissão
 * daquele endereço. Antes, qualquer colaborador abria qualquer URL — o menu
 * escondia o item, mas o endereço digitado entrava.
 */
function PermissionRoute() {
  const { pathname } = useLocation()
  const { can, isAdmin } = useAuthority()
  const needed = permissionForPath(pathname)

  if (isAdmin || !needed || can(needed)) return <Outlet />
  return <AccessDeniedPage needed={needed} />
}

/** Layout-route que só libera o grupo para os tipos permitidos. */
function RoleRoute({ allow }: { allow: string[] }) {
  const type = useAuthStore((s) => s.user?.type)
  if (type && allow.includes(type)) return <Outlet />
  return <Navigate to={homeFor(type)} replace />
}

export default function App() {
  return (
    <Routes>
      {/* Rotas PÚBLICAS (sem auth, fora do AppShell) — LP de vendas via consultor */}
      <Route path="/vendas/sucesso"        element={<SalesLinkSuccessPage />} />
      <Route path="/vendas/:slug"          element={<SalesLinkPlansPage />} />
      <Route path="/vendas/:slug/:planId"  element={<SalesLinkSubscribePage />} />

      <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/" element={<HomeRedirect />} />

        {/* ===== Painel ADMIN (ADMINISTRADOR / FUNCIONÁRIO) ===== */}
        {/* Dois portões: o tipo abre o painel, a permissão abre cada tela. */}
        <Route element={<RoleRoute allow={ADMIN_TYPES} />}>
        <Route element={<PermissionRoute />}>
        {/* Comercial — URLs hierárquicas /commercial/* */}
        <Route path="/commercial/companies"                 element={<CompaniesListPage />} />
        <Route path="/commercial/companies/novo"            element={<CompanyFormPage />} />
        <Route path="/commercial/companies/:id/editar"      element={<CompanyFormPage />} />
        <Route path="/commercial/companies/:id/faturas"     element={<CompanyInvoicesPage />} />

        <Route path="/plans"                                element={<PlansListPage />} />
        <Route path="/plans/novo"                           element={<PlanFormPage />} />
        <Route path="/plans/:id/editar"                     element={<PlanFormPage />} />

        <Route path="/commercial/subscribers"               element={<SubscribersListPage />} />
        <Route path="/commercial/subscribers/novo"          element={<SubscriberFormPage />} />
        <Route path="/commercial/subscribers/:id/editar"    element={<SubscriberFormPage />} />
        <Route path="/commercial/subscribers/:id/faturas"   element={<SubscriberInvoicesPage />} />

        <Route path="/commercial/discount-coupons"               element={<DiscountCouponsListPage />} />
        <Route path="/commercial/discount-coupons/novo"          element={<DiscountCouponFormPage />} />
        <Route path="/commercial/discount-coupons/:id/editar"    element={<DiscountCouponFormPage />} />
        <Route path="/commercial/proposals"                      element={<ProposalsListPage />} />
        <Route path="/commercial/proposals/novo"                 element={<ProposalFormPage />} />
        <Route path="/commercial/proposals/kanban"               element={<ProposalKanbanPage />} />
        <Route path="/commercial/proposals/:id/editar"           element={<ProposalFormPage />} />

        <Route path="/commercial/reports"           element={<Navigate to="/commercial/reports/general" replace />} />
        <Route path="/commercial/reports/general"   element={<ReportsGeneralPage />} />
        <Route path="/commercial/reports/providers" element={<ProvidersReportPage />} />
        <Route path="/commercial/reports/age"       element={<ReportsAgePage />} />
        <Route path="/commercial/reports/location"  element={<ReportsLocationPage />} />

        {/* Redirects das rotas curtas antigas */}
        <Route path="/companies"        element={<Navigate to="/commercial/companies" replace />} />
        <Route path="/subscribers"      element={<Navigate to="/commercial/subscribers" replace />} />
        <Route path="/discount-coupons" element={<Navigate to="/commercial/discount-coupons" replace />} />

        {/* Financeiro */}
        <Route path="/revenues"               element={<RevenuesListPage />} />
        <Route path="/revenues/projection"    element={<RevenueProjectionPage />} />
        <Route path="/revenues/:id"           element={<RevenueDetailPage />} />
        <Route path="/financial/reports"             element={<Navigate to="/financial/reports/earnings" replace />} />
        <Route path="/financial/reports/earnings"    element={<RevenueReportEarningsPage />} />
        <Route path="/financial/reports/commissions" element={<RevenueReportCommissionsPage />} />

        {/* Credenciamento — operacional */}
        <Route path="/credentialing/providers"             element={<CredentialingProvidersPage />} />
        <Route path="/credentialing/providers/novo"        element={<CredentialingProviderFormPage />} />
        <Route path="/credentialing/providers/:id/editar"  element={<CredentialingProviderFormPage />} />
        <Route path="/credentialing/specialty-search"      element={<CredentialingSpecialtyFilterPage />} />
        <Route path="/credentialing/reports/dashboard"      element={<CredentialingReportDashboardPage />} />
        <Route path="/credentialing/reports/commissions"    element={<CredentialingReportCommissionsPage />} />
        <Route path="/credentialing/reports/map"            element={<CredentialingReportMapPage />} />
        <Route path="/credentialing/kanban"                element={<CredentialingKanbanPage />} />
        <Route path="/credentialing/dashboard"             element={<CredentialingDashboardPage />} />

        {/* Administração */}
        <Route path="/administractive/hierarchies" element={<HierarchiesPage />} />
        <Route path="/administractive/hierarchies/novo" element={<HierarchyFormPage />} />
        <Route path="/administractive/hierarchies/:id/editar" element={<HierarchyFormPage />} />
        <Route path="/administractive/users"       element={<UsersPage />} />
        <Route path="/administractive/users/novo"   element={<UserFormPage />} />
        <Route path="/administractive/users/:id/editar" element={<UserFormPage />} />

        {/* Settings — regra global: URL acompanha hierarquia (settings/workflow/...) */}
        <Route path="/settings"                                element={<SettingsIndexPage />} />
        <Route path="/settings/parametros"                     element={<SettingsParametersPage />} />
        <Route path="/settings/workflow/banks"                 element={<SettingsBanksPage />} />
        <Route path="/settings/workflow/marital-statuses"      element={<SettingsMaritalStatusesPage />} />
        <Route path="/settings/workflow/degree-relationships"  element={<SettingsDegreeRelationshipsPage />} />
        <Route path="/settings/commercial/service-providers"   element={<SettingsServiceProvidersPage />} />
        <Route path="/settings/commercial/report-templates"            element={<SettingsReportTemplatesPage />} />
        <Route path="/settings/commercial/report-templates/novo"       element={<SettingsReportTemplateFormPage />} />
        <Route path="/settings/commercial/report-templates/:id/editar" element={<SettingsReportTemplateFormPage />} />
        <Route path="/settings/commercial/proposal-pricing"    element={<SettingsProposalPricingPage />} />
        <Route path="/settings/commercial/proposal-setup"      element={<SettingsProposalSetupPage />} />
        <Route path="/settings/commercial/proposal-catalog"    element={<SettingsProposalCatalogPage />} />
        <Route path="/settings/support/whatsapp"               element={<SettingsSupportWhatsAppPage />} />
        <Route path="/settings/integrations/medicar"           element={<SettingsIntegrationsMedicarPage />} />
        <Route path="/settings/commercial/proposal-templates"  element={<SettingsProposalTemplatesPage />} />
        <Route path="/settings/commercial/proposal-stages"     element={<ProposalStagesPage />} />
        <Route path="/settings/commercial/services"            element={<SettingsServicesPage />} />
        <Route path="/settings/commercial/manual-blocks"       element={<SettingsManualBlocksPage />} />
        <Route path="/settings/commercial/products"            element={<ProductsListPage />} />
        <Route path="/settings/commercial/products/novo"       element={<ProductFormPage />} />
        <Route path="/settings/commercial/products/:id/editar" element={<ProductFormPage />} />
        <Route path="/settings/workflow/specialties"           element={<WorkflowSpecialtiesPage />} />
        <Route path="/settings/workflow/tests"                 element={<WorkflowTestsPage />} />

        {/* Settings → Credenciamento */}
        <Route path="/settings/credentialing/types"                 element={<CredentialingTypesPage />} />
        <Route path="/settings/credentialing/steps"                 element={<CredentialingStepsPage />} />
        <Route path="/settings/credentialing/commission-structures"               element={<CredentialingCommissionStructuresPage />} />
        <Route path="/settings/credentialing/commission-structures/novo"          element={<CredentialingCommissionStructureFormPage />} />
        <Route path="/settings/credentialing/commission-structures/:id/editar"    element={<CredentialingCommissionStructureFormPage />} />

        {/* Settings → Comercial */}
        <Route path="/settings/commercial/commission-structures"                  element={<CommissionStructuresListPage />} />
        <Route path="/settings/commercial/commission-structures/novo"             element={<CommissionStructureFormPage />} />
        <Route path="/settings/commercial/commission-structures/:id/editar"       element={<CommissionStructureFormPage />} />
        <Route path="/settings/commercial/modalities"                             element={<SettingsModalitiesPage />} />

        {/* Redirecionamentos de URLs antigas (links externos / bookmarks) */}
        <Route path="/workflow/specialties"                 element={<Navigate to="/settings/workflow/specialties" replace />} />
        <Route path="/workflow/tests"                       element={<Navigate to="/settings/workflow/tests" replace />} />
        <Route path="/settings/banks"                       element={<Navigate to="/settings/workflow/banks" replace />} />
        <Route path="/settings/marital-statuses"            element={<Navigate to="/settings/workflow/marital-statuses" replace />} />
        <Route path="/settings/degree-relationships"        element={<Navigate to="/settings/workflow/degree-relationships" replace />} />
        <Route path="/credentialing/types"                  element={<Navigate to="/settings/credentialing/types" replace />} />
        <Route path="/credentialing/steps"                  element={<Navigate to="/settings/credentialing/steps" replace />} />
        <Route path="/credentialing/commission-structures"  element={<Navigate to="/settings/credentialing/commission-structures" replace />} />
        <Route path="/commission-structures"                element={<Navigate to="/settings/commercial/commission-structures" replace />} />

        {/* Agendamentos médicos */}
        <Route path="/medical-appointments"   element={<MedicalAppointmentsListPage />} />
        <Route path="/support/inbox"          element={<SupportInboxPage />} />
        <Route path="/support/settings"       element={<SupportSettingsPage />} />

        {/* Integrações */}
        <Route path="/integrations/medicar"             element={<IntegrationsMedicarPage />} />
        <Route path="/financial/duplicate-invoices"     element={<IntegrationsDuplicateInvoicesPage />} />
        <Route path="/integrations/error-logs"          element={<IntegrationsErrorLogsPage />} />
        <Route path="/integrations/backups"             element={<IntegrationsBackupsPage />} />
        <Route path="/integrations/asaas/ungenerated"   element={<AsaasUngeneratedInvoicesPage />} />
        <Route path="/integrations/asaas/charge-finder" element={<AsaasChargeFinderPage />} />

        {/* Links > Catálogo (admin/colaborador) */}
        <Route path="/catalog"                element={<MyCompanyCatalogPage />} />
        </Route>
        </Route>

        {/* ===== Painel EMPRESA ===== */}
        <Route element={<RoleRoute allow={EMPRESA_TYPES} />}>
        {/* My Company (escopo EMPRESA) */}
        <Route path="/my-company/dashboard"               element={<MyCompanyDashboardPage />} />
        <Route path="/my-company/invoices"                element={<MyCompanyInvoicesPage />} />
        <Route path="/my-company/invoices/:id"            element={<MyCompanyInvoiceDetailPage />} />
        <Route path="/my-company/collaborators"           element={<MyCompanyCollaboratorsPage />} />
        <Route path="/my-company/collaborators/aplicar-cupom" element={<MyCompanyBatchCouponPage />} />
        <Route path="/my-company/collaborators/novo"       element={<CollaboratorFormPage />} />
        <Route path="/my-company/collaborators/:id/editar" element={<CollaboratorFormPage />} />
        <Route path="/my-company/branches"                element={<MyCompanyBranchesPage />} />
        <Route path="/my-company/branches/novo"           element={<BranchFormPage />} />
        <Route path="/my-company/branches/:id/editar"     element={<BranchFormPage />} />
        <Route path="/my-company/departments"             element={<MyCompanyDepartmentsPage />} />
        <Route path="/my-company/departments/novo"        element={<DepartmentFormPage />} />
        <Route path="/my-company/departments/:id/editar"  element={<DepartmentFormPage />} />
        <Route path="/my-company/catalog"                 element={<MyCompanyCatalogPage />} />
        </Route>

        {/* ===== Painel PARCEIRO (diretor/supervisor/afiliado/vendedor) ===== */}
        <Route element={<RoleRoute allow={PARTNER_TYPES} />}>
        <Route path="/partner/dashboard"            element={<PartnerDashboardPage />} />
        <Route path="/partner/invoices"             element={<PartnerInvoicesPage />} />
        <Route path="/partner/invoices/:id"         element={<PartnerInvoiceDetailPage />} />
        <Route path="/partner/reports"              element={<Navigate to="/partner/reports/earnings" replace />} />
        <Route path="/partner/reports/earnings"     element={<PartnerReportEarningsPage />} />
        <Route path="/partner/reports/commissions"  element={<PartnerReportCommissionsPage />} />
        <Route path="/partner/network"              element={<PartnerNetworkPage />} />
        <Route path="/partner/network/novo"         element={<PartnerNetworkFormPage />} />
        <Route path="/partner/network/:id/editar"   element={<PartnerNetworkFormPage />} />
        <Route path="/partner/commission-models"          element={<PartnerCommissionModelsPage />} />
        <Route path="/partner/commission-models/novo"     element={<PartnerCommissionModelFormPage />} />
        <Route path="/partner/commission-models/:id/editar" element={<PartnerCommissionModelFormPage />} />
        <Route path="/partner/sales-link"           element={<PartnerSalesLinkPage />} />
        <Route path="/partner/catalog"              element={<MyCompanyCatalogPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
